import React, { useState, useEffect, useCallback } from "react";
import { X, Cookie, Settings } from "lucide-react";
import { Button } from "./ui/button";

const COOKIE_CONSENT_KEY = "responsia_cookie_consent";

// Google Analytics Measurement ID - Replace with your actual GA4 ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";

// Initialize Google Analytics
const initializeGA = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.gaInitialized) return;
  
  // Create and inject the gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure'
  });
  
  window.gaInitialized = true;
  console.log('[Cookie Consent] Google Analytics initialized');
};

// Remove Google Analytics cookies
const removeGACookies = () => {
  const cookies = document.cookie.split(';');
  const gaCookies = ['_ga', '_gid', '_gat', '_ga_'];
  
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    if (gaCookies.some(gac => cookieName.startsWith(gac))) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  console.log('[Cookie Consent] GA cookies removed');
};

// Apply consent to tracking scripts
const applyConsentToTracking = (preferences) => {
  if (typeof window === 'undefined') return;
  
  if (preferences.analytics) {
    // User accepted analytics - initialize GA
    initializeGA();
  } else {
    // User refused analytics - remove GA cookies
    removeGACookies();
    // Disable GA if it was initialized
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  }
  
  if (preferences.marketing) {
    // User accepted marketing - you can add Facebook Pixel, etc. here
    console.log('[Cookie Consent] Marketing cookies accepted');
    // Initialize marketing scripts here when needed
    // Example: initializeFacebookPixel();
  } else {
    console.log('[Cookie Consent] Marketing cookies denied');
    // Remove marketing cookies if needed
  }
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        // Apply saved preferences to tracking
        applyConsentToTracking(savedPreferences);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  // Listen for custom event to open settings
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
      setShowBanner(false);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const savePreferences = useCallback((prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    // Apply preferences to tracking scripts
    applyConsentToTracking(prefs);
  }, []);

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg animate-in slide-in-from-bottom duration-300">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-700 text-sm">
                    Nous utilisons des cookies pour améliorer votre expérience sur notre site. 
                    Les cookies essentiels sont nécessaires au fonctionnement du site. 
                    Vous pouvez personnaliser vos préférences à tout moment.
                  </p>
                  <button 
                    onClick={() => setShowSettings(true)}
                    className="text-violet-600 hover:underline text-sm mt-1"
                  >
                    En savoir plus
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="flex-1 md:flex-none"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Personnaliser
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptEssential}
                  className="flex-1 md:flex-none"
                >
                  Refuser
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="flex-1 md:flex-none bg-violet-600 hover:bg-violet-700"
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-violet-600" />
                  Gestion des cookies
                </h2>
                <button 
                  onClick={() => {
                    setShowSettings(false);
                    if (!localStorage.getItem(COOKIE_CONSENT_KEY)) {
                      setShowBanner(true);
                    }
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-slate-600 text-sm mb-6">
                Personnalisez vos préférences en matière de cookies. Les cookies essentiels 
                sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.
              </p>

              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-slate-900">Cookies essentiels</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Nécessaires au fonctionnement du site (authentification, sécurité).
                      </p>
                    </div>
                    <div className="bg-violet-100 text-violet-700 text-xs font-medium px-2 py-1 rounded">
                      Toujours actifs
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">Cookies analytiques</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Nous aident à comprendre comment vous utilisez le site pour l'améliorer.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    </label>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">Cookies marketing</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Utilisés pour vous proposer des contenus personnalisés.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={acceptEssential}
                  className="flex-1"
                >
                  Refuser tout
                </Button>
                <Button
                  onClick={saveCustomPreferences}
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                >
                  Enregistrer mes choix
                </Button>
              </div>

              <p className="text-xs text-slate-400 text-center mt-4">
                Vous pouvez modifier vos préférences à tout moment via le lien "Gérer les cookies" en bas de page.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Hook to open cookie settings from anywhere
export const useCookieSettings = () => {
  const openCookieSettings = () => {
    // Dispatch event to open settings modal without removing consent
    window.dispatchEvent(new Event('openCookieSettings'));
  };
  
  return { openCookieSettings };
};

// Export a function to open settings (for use outside React)
export const openCookieSettings = () => {
  window.dispatchEvent(new Event('openCookieSettings'));
};

// Export function to get current consent status
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      return JSON.parse(consent);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

// Export function to check if analytics is allowed
export const isAnalyticsAllowed = () => {
  const consent = getCookieConsent();
  return consent?.analytics === true;
};

// Export function to check if marketing is allowed
export const isMarketingAllowed = () => {
  const consent = getCookieConsent();
  return consent?.marketing === true;
};

export default CookieConsent;
